import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-31" data-mp="217k7hlu9cinz">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-32" data-mp="1wbwk9bzc5zl4">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-33" data-mp="1id5ag1rib02s">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-34" data-mp="2eicklk7cec24">
              <div className="flex items-center gap-3" data-id="element-35" data-mp="1lu8dqvd1bsxu">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-36" data-mp="1lv07e0n2nglh">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-37" data-mp="1qs9vncyq5m7q" />
                </div>
                <div data-id="element-38" data-mp="1uvy7i7ue33ys">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-39" data-mp="1ut25a7bidt4j">
                    Snakker · LEO Pharma
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-40" data-mp="3690kksxa5be7">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-41" data-mp="125fk6xjncjm0">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-42" data-mp="1b7ylhd7sao65" />
              </button>
            </div>

            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-43" data-mp="nhuin82f6sg7">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-44" data-mp="jdwf8l62avhc">
                Think global, act local:{' '}
                <span className="text-[#6C2BD9]" data-id="element-45" data-mp="sg5nt5hrses3">
                  one campaign engine, many local executions
                </span>{' '}
                — Snakker centralizes the campaign complexity so LEO's markets
                can move faster, and gives HQ full visibility across every
                brand, market and campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-46" data-mp="2bd900e031oex">
                The Pressure Is Rising
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-47" data-mp="22fqb2yjbipxr">
                More brands, more markets, more channels, more campaigns, more
                competition. The result is{' '}
                <strong data-id="element-48" data-mp="2v32xn8phaxr2">more complexity for pharma</strong> and{' '}
                <strong data-id="element-49" data-mp="2z4xxs9vob539">more fatigue for HCPs</strong>. HCPs don't lack
                information — they lack time. What cuts through is short and
                relevant.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-50" data-mp="2l7j2wpmni53w">
                The Hidden Legacy Potential
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-51" data-mp="19p86tu6uyiir">
                Every product moves through <strong data-id="element-52" data-mp="1eq4fel25msui">R&amp;D</strong>,{' '}
                <strong data-id="element-53" data-mp="1emqbh8duebky">Launch</strong>, <strong data-id="element-54" data-mp="1tz9ftc9fmz94">Mature</strong> and{' '}
                <strong data-id="element-55" data-mp="1u010hs1hrckm">Legacy</strong>. Commercial attention concentrates on
                the newest assets, while the established portfolio quietly
                carries the volume. The strategic move is to{' '}
                <em data-id="element-56" data-mp="1l4bgnnfoyq9c">soften the cliff and extend the tail</em> — where small
                improvements compound into significant portfolio value.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-57" data-mp="2cwap39a0rliv">
                LEO's Revenue Mix Today
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-58" data-mp="23wnkyfpwo1m4">
                LEO delivered <strong data-id="element-59" data-mp="23yqjxp91d52z">DKK 13.5bn</strong> revenue in 2025.{' '}
                <strong data-id="element-60" data-mp="1yx19obtn0vfy">
                  Roughly 76% comes from brands past exclusivity
                </strong>{' '}
                — Established dermatology (DKK 8.0bn, +2% CER) plus Critical
                Care (DKK 2.3bn) — while Strategic brands (DKK 3.0bn, +48% CER)
                take nearly all the commercial investment. That established base
                funds innovation, spans{' '}
                <strong data-id="element-61" data-mp="iebpkchm09qk">25+ promoted brands across 70+ markets</strong> (31
                affiliates), and is the part of the business that can least
                afford traditional agency campaigns.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-62" data-mp="s5pxn8nmmhvb">
                The Value of Small Improvements
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-63" data-mp="11306yogvvwxl">
                Applied to LEO's own numbers, even a{' '}
                <strong data-id="element-64" data-mp="164602neghm7n">0.5–5% improvement</strong> across the established
                portfolio is worth tens to hundreds of millions of kroner per
                year. At DKK 13.5bn revenue and a 76% established share, a{' '}
                <strong data-id="element-65" data-mp="3ctuc3ffc3qi8">1% improvement equals roughly DKK 103m annually</strong>{' '}
                — value that is currently unmanaged and under-optimized across
                25+ brands and 70+ markets.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-66" data-mp="2zuv3de3sqw9p">
                Relevance Is Local — And Time Is the Enemy
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-67" data-mp="39sivrxdxjrop">
                Markets need content that reflects local realities, priorities
                and HCP needs. Global delivery is often too slow, too generic
                and too far removed from what local teams need now. The result
                is a{' '}
                <strong data-id="element-68" data-mp="1bebvju2fah2">
                  growing gap between global campaign development and local
                  market execution
                </strong>{' '}
                — and it widens with every new brand, market and campaign cycle.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-69" data-mp="6bixfo9tyve5">
                Scale and Relevance Have Been an Oxymoron
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-70" data-mp="oxqag0tkqhdc">
                The <strong data-id="element-71" data-mp="txmiqc9b0uyp">agency model</strong> works for a handful of major
                campaigns, but at scale it is too expensive for high volumes,
                too slow for frequent local adaptation, too fragmented across
                brands and markets, too dependent on repeated briefing cycles
                and too hard to measure consistently across countries.{' '}
                <strong data-id="element-72" data-mp="b0bm6zf24fmy">Internalization</strong> adds ownership and output, but
                risks volume without strategic depth, production without
                continuous optimization, fixed resources regardless of demand
                and limited access to specialist creative capability.
                Internalization solves part of the capacity problem — not the
                relevance, engagement or performance problem.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-73" data-mp="3ogsonlirfhrd">
                Why Snakker
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-74" data-mp="2tjw6jpn7fhw">
                Snakker was born to solve exactly this. Thirty years of pharma
                campaign models turned into AI workflows: storyboarding and
                creation in minutes rather than months, producing short,
                engaging, TA-relevant campaigns ready for deployment. We combine{' '}
                <strong data-id="element-75" data-mp="3g6vcnzg0jvut">deep pharma fluency</strong> in regulated environments,{' '}
                <strong data-id="element-76" data-mp="3b8ad828l5g7f">a proprietary interactive engagement platform</strong>,{' '}
                <strong data-id="element-77" data-mp="3b5xuhmv071tb">local distribution capability</strong> and a{' '}
                <strong data-id="element-78" data-mp="3789y0fdnx0zb">closed behavioural feedback loop</strong> — with master
                agreements already in place across 15 of the top 30 healthcare
                companies globally.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-79" data-mp="2xam1bjwhxjei">
                You Have to Centralize to Localize
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-80" data-mp="1hoa0ax5qkf4s">
                <strong data-id="element-81" data-mp="1moxrbitysvz0">Centralize</strong> what should not be rebuilt in every
                market: campaign strategy, scientific narrative, core claims,
                brand standards, reusable assets, production workflows,
                compliance governance, measurement standards and performance
                learning. <strong data-id="element-82" data-mp="yyah2qwktu9a">Decentralize</strong> what only markets can
                own: campaign activation, local insight, language, cultural
                context, channel selection, market-specific emphasis, local
                calls to action and local compliance governance. The goal is not
                to centralize local decisions — it is to{' '}
                <strong data-id="element-83" data-mp="b7oidq8gpmby">centralize the complexity</strong> so local teams can
                move faster.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-84" data-mp="2b9mg4sa09iti">
                One Campaign Engine, Many Local Executions
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-85" data-mp="2k9rob4n0wl8q">
                Snakker becomes the <strong data-id="element-86" data-mp="2k7onrqo8v9pr">central campaign office</strong>. We
                build the master campaign — strategy, hook, email and banner
                assets, teaser Snaks, trailer Snaks, calls to action,
                measurement framework and optimization recommendations. Local
                teams then adapt and activate it for their market. The result:{' '}
                <strong data-id="element-87" data-mp="21ii1xus5m1zq">
                  global consistency, local relevance, faster execution
                </strong>
                .
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-88" data-mp="18uvycmixexcv">
                A New Category: Campaign Service Management
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-89" data-mp="1drcm3fm7r9pf">
                Snakker is not an agency and not a SaaS licence. It is{' '}
                <strong data-id="element-90" data-mp="11atqeflm7fgr">Campaign Service Management</strong> — managed pharma
                campaign execution powered by software economics. We deliver the
                outcome, not the tool.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-91" data-mp="s9diqfm6prg8">
                The Operating Model — A Loop, Not a Project
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-92" data-mp="ik3r10v96asn">
                Four steps that feed each other, cycle after cycle:{' '}
                <strong data-id="element-93" data-mp="dm23a2mvtnau">leverage existing approved assets</strong>,{' '}
                <strong data-id="element-94" data-mp="8n7fema258jv">deploy rapidly in local markets</strong>,{' '}
                <strong data-id="element-95" data-mp="4kugagptg8w2">capture behavioural data</strong> on real HCP
                engagement, and <strong data-id="element-96" data-mp="4mner8q9zmsz">optimize continuously</strong> so every
                interaction sharpens the next campaign — and every market learns
                from the others.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-97" data-mp="3m5w240uxgyyc">
                HQ Gets the Control Tower
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-98" data-mp="3c6nahenjbwf5">
                A cross-brand, cross-region dashboard for every Snak and
                campaign in LEO: <strong data-id="element-99" data-mp="8omxtm71b2v7">what is live</strong>,{' '}
                <strong data-id="element-100" data-mp="8py5vy7iiws4">where</strong>, <strong data-id="element-101" data-mp="8qpzj3hjukfr">what it looks like</strong>,{' '}
                <strong data-id="element-102" data-mp="dmoho9cab67q">how it performs</strong>,{' '}
                <strong data-id="element-103" data-mp="dpj1pzrdyp0m">which markets outperform</strong>,{' '}
                <strong data-id="element-104" data-mp="3v3oiz9gasenu">what should improve</strong> and{' '}
                <strong data-id="element-105" data-mp="3xdbaugs8fnj">what can be reused as best practice</strong> — plus
                on/off control per campaign. More control for HQ, more speed for
                local markets.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-106" data-mp="weplzr7tvooj">
                What a Campaign Looks Like
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-107" data-mp="2h29y5gzupfm8">
                Snakker acts as the operating layer between strategy and
                execution: campaigns built from approved content, turned into
                short interactive <strong data-id="element-108" data-mp="2lutdoji3mkto">Snaks™</strong>, published across
                channels, measured on every interaction and improved campaign
                after campaign. The same format adapts across the HCP journey —
                awareness, education, engagement, and conversion &amp; loyalty.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-109" data-mp="276p0fv1tf1ia">
                Fast Marketing™ Economics
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-110" data-mp="3illtrh518cvg">
                Cost is built from three transparent units that improve with
                volume: a <strong data-id="element-111" data-mp="3dlpozfoigheu">Master Snak from €2,900 down to €1,900</strong>{' '}
                as campaign volume scales, <strong data-id="element-112" data-mp="csl7dyqp9c8u">country localization</strong>{' '}
                from €999 with up to 50% volume benefit, and{' '}
                <strong data-id="element-113" data-mp="hsh1n69lhpb7">country activation and insights</strong> from €99 per
                month per country on the same curve. A traditional digital
                campaign for one brand in one country costs{' '}
                <strong data-id="element-114" data-mp="rm7q4b5q1zqw">€15,000–€19,000</strong>; the Snakker model delivers a
                fully managed localized deployment in the{' '}
                <strong data-id="element-115" data-mp="10o756xx6etr7">low thousands</strong> — and scales predictably from one
                brand in one market to the full portfolio.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-116" data-mp="15ibgftdh5ny3">
                The Proposition
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-117" data-mp="1ffg1tqzhxfdu">
                <strong data-id="element-118" data-mp="1jfgqe4gb76ym">Centralize the campaign engine.</strong>{' '}
                <strong data-id="element-119" data-mp="1jdnkwshfmss7">Decentralize local execution.</strong>{' '}
                <strong data-id="element-120" data-mp="33yk9ae2cim8y">Give HQ full visibility.</strong> Snakker combines scale
                and relevance in one operating model.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-121" data-mp="2ux2wp5o5d2p0">
                "But Doesn't This Make Us Dependent?"
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-122" data-mp="2q042yo8eaxrc">
                Every operating model depends on capabilities — agencies,
                internal teams, technology, production partners, data providers,
                compliance systems. The question is whether that dependency is{' '}
                <strong data-id="element-123" data-mp="3dtvpl0tek1it">
                  transparent, governed, measurable, replaceable, cost-controlled
                </strong>{' '}
                and backed by full access to outputs. LEO receives all campaign
                content, all Snaks and approved assets, all storyboards, all
                local versions, all campaign data, all performance reporting,
                standardized workflows, visible costs, agreed service levels and
                the HQ Control Tower. This is a{' '}
                <strong data-id="element-124" data-mp="23roz1kbzxx0q">managed partnership, not a black box</strong>.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-125" data-mp="1pxxoc8mj473f">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-126" data-mp="1fzi50rtqu24h">
                Snakker provides the engine — LEO retains the steering wheel:
                global consistency, local relevance and faster execution across
                25+ brands and 70+ markets.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}